import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { resolveFlowControlExpandSegmentMeta } from '@/helpers/flow-control-expand-segment-meta.helper';
import {
  findFlowSlotForControlExpandId,
  resolveFlowControlActiveExpandId,
} from '@/helpers/flow-control-playing-segment.helper';
import type { FlowControlMediaExpandSource } from '@/state/stores/use-flow-control-media-expand-store';

export type FlowControlOverlayHeader = {
  title: string;
  subtitle?: string;
};

export function resolveFlowControlSlotMobileTitle(
  slot: FlowSlotDto,
  t: (key: string, options?: { defaultValue?: string }) => string,
): string {
  if (slot.contentType === 'Session') {
    const sessionTitle =
      typeof slot.content === 'object' && slot.content?.title ? String(slot.content.title) : undefined;
    return sessionTitle || `Slot content ${t('common.notPublishedYet')}.`;
  }
  if (slot.contentType === 'Break') {
    return slot.title || t('common.N/A');
  }
  return `Slot content ${t('common.notPublishedYet')}.`;
}

export function resolveFlowControlSlotMobileSubtitle(slot: FlowSlotDto, flow: FlowDto | undefined): string | undefined {
  const flowSlot = flow?.slot;
  if (!flowSlot || slot.computedOffsetMinutes === undefined || flowSlot.startOffsetMinutes === undefined) {
    return undefined;
  }
  const startOffset = flowSlot.startOffsetMinutes + slot.computedOffsetMinutes;
  const duration = slot.computedDurationMinutes ?? slot.plannedDurationMinutes ?? 0;
  const endOffset = startOffset + duration;
  return `${minuteOffsetToTime(startOffset)} - ${minuteOffsetToTime(endOffset)} (${duration} minutes)`;
}

export function resolveFlowControlOverlayHeader({
  activeExpandId,
  flow,
  flowPlan,
  fallbackSlot,
  t,
  controlSubtitle,
}: {
  activeExpandId: string | null;
  flow: FlowDto | undefined;
  flowPlan: FlowDto | undefined;
  fallbackSlot?: FlowSlotDto;
  t: (key: string, options?: { defaultValue?: string }) => string;
  controlSubtitle: string;
}): FlowControlOverlayHeader {
  const flowToUse = flowPlan ?? flow;

  if (!activeExpandId || !flowToUse) {
    return {
      title: flowToUse?.title ?? '',
      subtitle: controlSubtitle,
    };
  }

  const resolvedSlot =
    findFlowSlotForControlExpandId(flowPlan?.slots, activeExpandId) ??
    findFlowSlotForControlExpandId(flow?.slots, activeExpandId) ??
    fallbackSlot ??
    null;

  const meta = resolveFlowControlExpandSegmentMeta(activeExpandId, resolvedSlot, t, flowPlan?.slots);

  if (resolvedSlot && (meta?.segmentKind === 'session' || meta?.segmentKind === 'break')) {
    return {
      title: resolveFlowControlSlotMobileTitle(resolvedSlot, t),
      subtitle: resolveFlowControlSlotMobileSubtitle(resolvedSlot, flowToUse),
    };
  }

  if (meta) {
    return { title: meta.label };
  }

  return {
    title: flowToUse.title ?? '',
    subtitle: controlSubtitle,
  };
}

export function resolveFlowControlOverlayActiveExpandId(
  expandId: string | null,
  playingExpandId: string | null,
  fallbackSlotId?: string,
  expandSource: FlowControlMediaExpandSource = null,
): string | null {
  return resolveFlowControlActiveExpandId(expandId, playingExpandId, fallbackSlotId, expandSource);
}
